import React from 'react';
import { CompanyDetailInfo, NavOption } from '../types/company';

interface PrivateMarketsProductTitleProps {
  id?: string;
  selectedNav: string;
  companyDetailInfo: CompanyDetailInfo | null;
  navOptions: NavOption[];
  onChangeNav: (value: string) => void;
  loading: boolean;
}

const formatDollar = (value: number | null, position?: number, unit?: string) => {
  if (value === null) return '-';
  if (position) {
    return `$${(value / position).toFixed(2)}${unit || ''}`;
  }
  return `$${value.toFixed(2)}${unit || ''}`;
};

const PrivateMarketsProductTitle: React.FC<PrivateMarketsProductTitleProps> = ({
  id,
  selectedNav,
  companyDetailInfo,
  navOptions,
  onChangeNav,
  loading,
}) => {
  if (loading) {
    return (
      <div className="sticky top-[118px] w-full z-10 bg-[#171717] pb-6" id={id}>
        <div className="max-w-[950px] mx-auto text-white py-6 px-12">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!companyDetailInfo) {
    return null;
  }

  return (
    <div className="sticky top-[115px] sm:top-[102px] md:top-[137px] lg:top-[149px]  w-full z-10 bg-[#171717] pb-6" id={id}>
      <nav className="max-w-[950px] mx-auto text-white py-2 sm:py-6 md:py-6 px-4 sm:px-12 md:px-12">
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <div className="flex items-center">
              <img
                src={companyDetailInfo.logoLink}
                alt={companyDetailInfo.name}
                className="h-10 w-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-full mr-2 md:mr-4"
              />
              <div className="sm:flex items-center">
                <h1 className="text-sm sm:text-2xl md:text-3xl lg:text-3xl font-bold mr-2 sm:mr-3 md:mr-4">{companyDetailInfo.name}</h1>
                <div className="flex flex-col text-[8px] sm:text-sm md:text-base">
                  {companyDetailInfo.isMarketActive && (
                      <span className="text-[#3a8aa0]">Active market</span>
                  )}
                  <span>{companyDetailInfo.category}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
              <span className="text-[8px] sm:text-base md:text-lg lg:text-xl font-bold">Last Round Valuation</span>
              <span className="text-[12px] sm:text-xl md:text-2xl">
                {formatDollar(companyDetailInfo.valuation, 1e9, 'B')}
              </span>
              <span className="text-[8px] sm:text-sm md:text-lg">
                {formatDollar(companyDetailInfo.totalFundingAmount, 1e9, 'B')}
                <span className="text-[10px] sm:text-base md:text-lg lg:text-xl"> Total Funding</span>
              </span>
          </div>
          <div className="flex flex-col">
              <span className="text-[8px] sm:text-base md:text-lg lg:text-xl font-bold">Last Round Share Price</span>
              <span className="text-[12px] sm:text-xl md:text-2xl">
                {formatDollar(companyDetailInfo.lastRoundPreferredSharePrice)}
              </span>
              <span className="text-[8px] sm:text-sm md:text-lg">
                {companyDetailInfo.lastRound}
                <span className="text-[10px] sm:text-base md:text-lg lg:text-xl"> Last Round</span>
              </span>
            </div>
        </div>
      </nav>

      <div className="max-w-[950px] mx-auto px-4 sm:px-12 md:px-12 grid grid-cols-3 gap-2 sm:gap-4">
        <div className="flex items-center justify-between">
          <select
            className="text-[8px] sm:text-base md:text-lg lg:text-xl w-full bg-transparent text-white border border-white rounded px-1 sm:px-2 py-1 sm:py-2"
            value={selectedNav}
            onChange={(e) => onChangeNav(e.target.value)}
          >
            {navOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
                className={selectedNav === option.value ? 'text-blue-500' : 'text-black'}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <button className="text-[8px] sm:text-base md:text-lg lg:text-xl w-full bg-blue-500 text-white px-1 sm:px-2 py-1 sm:py-2 rounded flex items-center justify-center border border-blue-500">
            <span className="mr-1 sm:mr-2">+</span>
            Express Interest
          </button>
        </div>

        <div className="flex text-[8px] sm:text-base md:text-lg lg:text-xl">
          <button className="flex-1 border border-white text-white px-1 sm:px-2 py-1 sm:py-2 rounded-l flex items-center justify-center">
            <span className="mr-1 sm:mr-2">+</span>
            Watch
          </button>
          <button className="flex-1 border border-white text-white px-1 sm:px-2 py-1 sm:py-2 flex items-center justify-center">
            <span className="mr-1 sm:mr-2">+</span>
            Share
          </button>
          <button className="flex-1 border border-white text-white px-1 sm:px-2 py-1 sm:py-2 rounded-r flex items-center justify-center">
            <span className="mr-1 sm:mr-2">+</span>
            Flag
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivateMarketsProductTitle;
